/**
 * `<EditorStatusBar>` — the editor's dirty / conflict / drop-outcome status affordance
 * (editor-chat.md §1.6, §2.5, §3.1).
 *
 * editor-chat.md §1.6 / §2.5: the editor *displays* dirty/conflicted state — it does **not**
 * own the state machine. This bar is that display: it renders the `EditorSaveState`
 * (clean / dirty / saving / saved / save-failed / conflicted) and, for the D-007 `conflicted`
 * state, a link to Corpus Health (where D-007/D-008 conflict resolution lives — editor-chat.md
 * §2.5). It also surfaces the most recent file-drop outcomes so the user always sees what
 * happened to a dropped file (editor-chat.md §3.1 — never a silent drop; conventions.md §3).
 *
 * It additionally shows the autocomplete freshness ("tags · titles refreshed N ago") — a small
 * transparency signal that the backend-fed completion data is current (editor-chat.md §2.2).
 *
 * Pure presentational — it takes the state and renders it; all the logic (the save sequence,
 * the drop routing) lives in `<EditorSurface>` and the extensions.
 */

import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes';
import type { SourceType } from '@/types';
import type { DropOutcome, EditorSaveState } from './editorTypes';

/** `<EditorStatusBar>` props — the state to display; no callbacks (it is pure presentational). */
export interface EditorStatusBarProps {
  /** The editor's current save state — drives the dirty/saved/conflicted label. */
  saveState: EditorSaveState;
  /** The note's source — surfaced so a Joplin note's editability is *visible* (D-007). */
  source: SourceType;
  /** When the autocomplete data was last refreshed — ISO-8601. */
  autocompleteRefreshedAt: string;
  /** How many tags are in the autocomplete cache. */
  tagCount: number;
  /** How many note titles are in the autocomplete cache. */
  titleCount: number;
  /** The most recent file-drop outcomes (newest first) — surfaced so a drop is never silent. */
  dropOutcomes: DropOutcome[];
}

/** A short, human label for the current save state — the bar's primary signal.
 *  Lowercase: the whole strip reads as one mono status line (U7). */
function saveStateLabel(state: EditorSaveState): { text: string; tone: string } {
  switch (state.status) {
    case 'clean':
      return { text: 'saved', tone: 'clean' };
    case 'dirty':
      return { text: 'unsaved changes', tone: 'dirty' };
    case 'saving':
      return { text: 'saving…', tone: 'saving' };
    case 'saved':
      // `locallyModifiedAt` non-null means a Joplin note was dirtied server-side (D-007) — a
      // useful, honest signal that the local edit diverged the note from Joplin.
      return {
        text:
          state.result.locallyModifiedAt !== null
            ? 'saved — marked locally modified'
            : 'saved',
        tone: 'clean',
      };
    case 'save-failed':
      return {
        text: state.retryable ? 'couldn’t save — will retry' : 'couldn’t save',
        tone: 'error',
      };
    case 'conflicted':
      // D-007: ingestion detected a both-sides edit. The bar SHOWS this and links to Corpus
      // Health; the editor does not resolve it (editor-chat.md §1.6 / §2.5).
      return { text: 'conflict — both sides edited', tone: 'conflict' };
  }
}

/** A human-readable line for one file-drop outcome (editor-chat.md §3.1). */
function dropOutcomeLabel(outcome: DropOutcome): string {
  switch (outcome.kind) {
    case 'markdown-import':
      return `Imported "${outcome.fileName}" as a new note.`;
    case 'attachment':
      return `Attached "${outcome.fileName}".`;
    case 'attachment-pending':
      return `"${outcome.fileName}" — ${outcome.reason}`;
    case 'rejected':
      return `"${outcome.fileName}" — ${outcome.reason}`;
  }
}

/**
 * The editor status bar. Renders the save-state label, the D-007 conflict link, the
 * autocomplete freshness, and the recent file-drop outcomes.
 */
export function EditorStatusBar({
  saveState,
  source,
  autocompleteRefreshedAt,
  tagCount,
  titleCount,
  dropOutcomes,
}: EditorStatusBarProps): JSX.Element {
  const label = saveStateLabel(saveState);
  const refreshed = new Date(autocompleteRefreshedAt);
  // Autocomplete freshness — the completion data is backend-fed and cached; this is the
  // transparency signal that it is current (editor-chat.md §2.2). A 1970 epoch means the
  // first fetch hasn't landed yet — say THAT, instead of the pre-U7 jumble of zero counts
  // and "refreshed loading…".
  const autocompleteText =
    refreshed.getTime() === 0
      ? 'autocomplete loading…'
      : `autocomplete: ${tagCount.toLocaleString()} tags / ${titleCount.toLocaleString()}` +
        ` titles · refreshed ${refreshed.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}`;

  // One coherent mono line: `saved · autocomplete: 1,512 tags / 444 titles · refreshed 12:04`
  // — fragments joined by · separators (U7; was two unstyled divs that ran together).
  const sep = (
    <span className="cm-aletheia-editor-statusbar__sep" aria-hidden="true">
      ·
    </span>
  );

  return (
    <div className="cm-aletheia-editor-statusbar" data-testid="editor-statusbar">
      <span
        className={`cm-aletheia-save-state cm-aletheia-save-state--${label.tone}`}
        role="status"
      >
        {label.text}
      </span>
      {/* D-007: a Joplin note is fully editable — make that *visible* rather than implying
          (by omission) that it might be read-only. There is no "edit in Joplin" affordance. */}
      {source === 'joplin' && (
        <>
          {sep}
          <span className="cm-aletheia-source-note">joplin-sourced — fully editable</span>
        </>
      )}
      {/* The D-007 conflict path — the editor does not resolve conflicts; it links to Corpus
          Health where D-007/D-008 resolution lives (editor-chat.md §2.5). */}
      {saveState.status === 'conflicted' && (
        <>
          {sep}
          <Link to={ROUTES.health} className="cm-aletheia-conflict-link">
            resolve in Corpus Health
          </Link>
        </>
      )}
      {/* A failed save keeps the buffer dirty (the save extension never loses it) — the
          message is the surfaced "couldn't save" guidance (editor-chat.md §4). */}
      {saveState.status === 'save-failed' && (
        <>
          {sep}
          <span className="cm-aletheia-save-error-detail">{saveState.message}</span>
        </>
      )}
      {sep}
      <span className="cm-aletheia-editor-statusbar__autocomplete">{autocompleteText}</span>

      {dropOutcomes.length > 0 && (
        <ul className="cm-aletheia-editor-statusbar__drops">
          {dropOutcomes.map((outcome, i) => (
            <li
              // The outcome list is a short, newest-first FIFO with no stable id — the index is
              // a fine key here (the list is tiny and only ever prepended/truncated).
              key={`${outcome.fileName}-${i}`}
              className={`cm-aletheia-drop-outcome cm-aletheia-drop-outcome--${outcome.kind}`}
            >
              {dropOutcomeLabel(outcome)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
